/**
 * Modal that displays a freshly-minted temp password ONCE. Per design.md
 * Decision 11: dismissable only via the explicit "Got it — I've saved this"
 * button — Esc and backdrop click are intercepted and no-op'd.
 *
 * `onClose` is the only way out. Callers should clear any cached server
 * response that contained `temporaryPassword` from their state when this
 * unmounts (the modal calls `onClose` exactly once).
 */
import { useEffect, useState } from "react";

export interface TempPasswordRevealProps {
  password: string;
  onClose: () => void;
}

export function TempPasswordReveal({ password, onClose }: TempPasswordRevealProps) {
  const [copied, setCopied] = useState(false);

  // Esc keypress is intercepted at window-level (capture phase) so it
  // doesn't propagate to the parent modal hierarchy.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      // Clipboard API may be unavailable (e.g., insecure context). Fall
      // back silently — the user can copy by selecting the text.
    }
  }

  return (
    <div
      data-testid="temp-password-backdrop"
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> needs imperative showModal() — this codebase uses controlled overlays
      role="dialog"
      aria-modal="true"
      aria-label="Temporary password"
      onClick={(e) => {
        // Backdrop click is intercepted — no dismiss.
        e.stopPropagation();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 24,
          width: 440,
          maxWidth: "calc(100vw - 32px)",
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Temporary password</h2>
        <p style={{ color: "#374151", fontSize: 13 }}>
          Share this password out-of-band (Slack/email). It is shown <strong>once</strong> — there
          is no way to retrieve it later.
        </p>
        <div
          style={{
            display: "flex",
            gap: 8,
            background: "#f3f4f6",
            border: "1px solid #d1d5db",
            borderRadius: 4,
            padding: "8px 12px",
            margin: "12px 0",
            fontFamily: "monospace",
            fontSize: 14,
          }}
        >
          <span style={{ flex: 1, wordBreak: "break-all" }}>{password}</span>
          <button
            type="button"
            onClick={copy}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              fontSize: 12,
              color: "#1e3a8a",
            }}
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 16px",
              background: "#1e3a8a",
              color: "#fff",
              border: 0,
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Got it — I've saved this
          </button>
        </div>
      </div>
    </div>
  );
}
